import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    FlatList,
    ListRenderItemInfo,
    View,
    useWindowDimensions,
} from 'react-native';

import { useIsFocused } from '@react-navigation/native';
import debounce from 'lodash.debounce';

import {
    CastCrewPerson,
    MixedItem,
    RBChannel,
    RBParsedRail,
    RBProgram,
} from 'store/railsBuilder/reducer.types';
import { RecordingItem } from 'store/recordings/reducer.types';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';
import { ContentType, NowNextData } from 'services/data-manager/types';

import usePrevious from 'utils/hooks/usePrevious';
import useViewableItems from 'utils/hooks/useViewableItems';
import {
    DEFAULT_ORIENTATION_TYPE,
    useDeviceOrientation,
} from 'utils/orientation';
import {
    isTabletOrWeb,
    isWeb,
    widthPercentageToDP,
} from 'utils/responsiveness';

import { EArrowType } from 'components/CustomButtons/ArrowButton';
import {
    CAST_ITEM_SIZE_WEB,
    CHANNEL_ITEM_SIZE_WEB,
    LANDSCAPE_ITEM_SIZE,
    STRIPE_MARGIN_WEB,
} from 'components/CustomButtons/RibbonTouchable/styles';
import useGetItemLayout from 'components/Ribbon/hooks/useGetItemLayout';
import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';

import RibbonItem from './RibonItem';
import { RibbonArrows } from './components/RibbonArrows';
import useScrollOffset from './hooks/useScrollOffset';
import useSnapOffset from './hooks/useSnapOffset';
import styles, { RIBBON_CONTAINER_PADDING } from './styles';

export type RibbonProps = {
    testID?: string;
    items: RBParsedRail['sections'];
    metadataType: ContentType;
    initialScrollIndex?: number | null;
    onPress?(item: RBParsedRail['sections'][number]): void;
    onShowPinDialog?(): void;
    onSetParentalControlCheckedCallback?(
        callback: undefined | (() => void),
    ): void;
    railTitle?: string;
    onEndReached?(): void;
} & OnUavAnalyticsProps &
    StandardUavProps;

function Ribbon({
    items = [],
    onRailsSelection,
    onDeepLinkTriggered,
    onThirdPartyAppStart,
    onPress,
    initialScrollIndex,
    metadataType,
    railId,
    templateId,
    testID,
    railTitle,
    onShowPinDialog,
    onSetParentalControlCheckedCallback,
    onEndReached,
}: RibbonProps) {
    const { visibleItems, viewabilityConfig, onViewableItemsChanged } =
        useViewableItems(items, metadataType);

    const snapOffsets = useSnapOffset(items, metadataType);
    const getItemLayout = useGetItemLayout(metadataType);

    const orientation = useDeviceOrientation();
    const prevOrientation = usePrevious(orientation);

    const [isPointerDisabled, setIsPointerDisabled] = useState(false);

    const flatlistRef =
        useRef<FlatList<RBParsedRail['sections'][number]>>(null);

    const lastItemRef = useRef<View>(null);
    const firstItemRef = useRef<View>(null);
    const lastItemsChangeDate = useRef(Date.now());

    const { scrollOffset, handleSetScrollOffset } = useScrollOffset();

    const { width } = useWindowDimensions();
    const containerWidth = width - RIBBON_CONTAINER_PADDING;

    const isFocused = useIsFocused();

    const containerStyles = useMemo(() => {
        if (items.length) {
            return styles.contentContainer;
        }

        return null;
    }, [items]);

    const listDomId = useMemo(
        () =>
            railId || templateId
                ? `ribbon-${templateId ?? 't'}-${railId ?? 'r'}`
                : undefined,
        [templateId, railId],
    );

    const getWebScrollLeft = useCallback((): number | undefined => {
        if (!isWeb || !listDomId) {
            return undefined;
        }

        const el = document.getElementById(listDomId) as HTMLElement | null;

        return el?.scrollLeft ?? undefined;
    }, [listDomId]);

    const handleOnEndReached = useCallback(
        ({ distanceFromEnd }) => {
            // In case of CastCrew stripe - when `items` data updates for another episode,
            // it was the case that when items.length is one or two item more than stripe could fit in the screen, then
            // onEndReached was triggered automatically.
            // To avoid this case - 400 ms delay after new data appeared was added, after that delay onEndReached in WEB is allowed.
            if (
                isWeb &&
                distanceFromEnd > 0 &&
                Date.now() - lastItemsChangeDate.current > 400
            ) {
                setAccessibilityFocusTo(lastItemRef);
            }

            onEndReached?.();
        },
        [onEndReached],
    );

    const handleOnStartReached = useCallback(() => {
        if (isWeb) {
            setAccessibilityFocusTo(firstItemRef);
        }
    }, []);

    const handleRibbonScroll = useCallback(
        ({
            nativeEvent: {
                contentOffset: { x },
            },
        }) => {
            if (x <= 0) {
                handleOnStartReached();
            }

            if (orientation === DEFAULT_ORIENTATION_TYPE) {
                handleSetScrollOffset(x);
            }
        },
        [handleSetScrollOffset, orientation],
    );

    const handleCalculateItemsPerScreen = useCallback(() => {
        if (metadataType === ContentType.ChannelItem) {
            return Math.floor(
                containerWidth /
                    (widthPercentageToDP(CHANNEL_ITEM_SIZE_WEB) +
                        STRIPE_MARGIN_WEB),
            );
        }

        if (metadataType === ContentType.CastPerson) {
            return Math.floor(
                containerWidth / (CAST_ITEM_SIZE_WEB + STRIPE_MARGIN_WEB),
            );
        }

        return Math.floor(
            containerWidth /
                (widthPercentageToDP(LANDSCAPE_ITEM_SIZE) + STRIPE_MARGIN_WEB),
        );
    }, [metadataType, containerWidth]);

    const handleArrowNavigationPress = useCallback(
        (type: EArrowType) => {
            const currentOffsetIndex = snapOffsets.findIndex(
                (offset) => offset >= scrollOffset,
            );
            const firstItemOffset = currentOffsetIndex === 0 ? 1 : 0;
            const nextOffsetIndex =
                type === 'fwd'
                    ? currentOffsetIndex +
                      handleCalculateItemsPerScreen() -
                      firstItemOffset
                    : currentOffsetIndex - handleCalculateItemsPerScreen();

            setIsPointerDisabled(true);

            flatlistRef.current?.scrollToOffset({
                offset: snapOffsets[nextOffsetIndex],
                animated: true,
            });
        },
        [handleCalculateItemsPerScreen, scrollOffset, snapOffsets],
    );

    const renderItem = useCallback(
        ({
            item,
            index,
        }: ListRenderItemInfo<RBParsedRail['sections'][number]>) => {
            return (
                <RibbonItem
                    ref={
                        index === 0
                            ? firstItemRef
                            : index === items.length - 1
                            ? lastItemRef
                            : undefined
                    }
                    index={index}
                    item={item}
                    metadataType={metadataType}
                    visibleItems={visibleItems}
                    templateId={templateId}
                    railId={railId}
                    onPress={onPress}
                    onRailsSelection={onRailsSelection}
                    onDeepLinkTriggered={onDeepLinkTriggered}
                    onThirdPartyAppStart={onThirdPartyAppStart}
                    onShowPinDialog={onShowPinDialog}
                    onSetParentalControlCheckedCallback={
                        onSetParentalControlCheckedCallback
                    }
                    totalNumberOfItems={items.length}
                    railTitle={railTitle}
                />
            );
        },
        [
            metadataType,
            visibleItems,
            onPress,
            onRailsSelection,
            railId,
            templateId,
            onDeepLinkTriggered,
            onThirdPartyAppStart,
            onShowPinDialog,
            onSetParentalControlCheckedCallback,
            items.length,
        ],
    );

    const debouncedSetIsPointerDisabled = useCallback(
        debounce(() => {
            setIsPointerDisabled(false);
        }, 100),
        [],
    );

    useEffect(() => {
        if (
            prevOrientation !== orientation &&
            orientation === DEFAULT_ORIENTATION_TYPE
        ) {
            setTimeout(
                () =>
                    flatlistRef.current?.scrollToOffset({
                        offset: scrollOffset,
                        animated: false,
                    }),
                0,
            );
        }
    }, [orientation, prevOrientation, railId, scrollOffset]);

    useEffect(() => {
        if (isWeb) {
            debouncedSetIsPointerDisabled();
        }
    }, [scrollOffset, debouncedSetIsPointerDisabled]);

    useEffect(() => {
        if (isWeb && isFocused) {
            const currentScrollLeft = getWebScrollLeft();

            if (
                currentScrollLeft !== undefined &&
                scrollOffset !== currentScrollLeft
            ) {
                flatlistRef.current?.scrollToOffset({
                    offset: scrollOffset,
                    animated: false,
                });
            }
        }
    }, [isFocused, getWebScrollLeft]);

    // initialScrollIndex > 0, in Web, hides list items with index < initialScrollIndex,
    // in result we need a workaround to scroll to the initialScrollIndex by using ref.current.scrollToIndex,
    // and hide list for the time while scrolling happens to avoid list "flickering" effect.
    const [webRibbonHidden, setWebRibbonHidden] = useState(
        isWeb && initialScrollIndex != null && initialScrollIndex > 0,
    );

    useLayoutEffect(() => {
        if (
            isTabletOrWeb &&
            initialScrollIndex != null &&
            initialScrollIndex > 0
        ) {
            setTimeout(() => {
                if (flatlistRef.current) {
                    flatlistRef.current.scrollToIndex({
                        index: initialScrollIndex,
                        animated: false,
                    });
                    setWebRibbonHidden(false);
                }
            }, 200);
        }
    }, []);

    useEffect(() => {
        lastItemsChangeDate.current = Date.now();
    }, [items]);

    return (
        <View
            testID={testID}
            pointerEvents={isPointerDisabled ? 'none' : 'auto'}
        >
            {isWeb && !!items.length && (
                <RibbonArrows
                    visibleItems={visibleItems}
                    itemsLength={items.length}
                    maxItemsOnScreen={handleCalculateItemsPerScreen()}
                    metadataType={metadataType}
                    initialScrollIndex={initialScrollIndex}
                    scrollOffset={scrollOffset}
                    snapOffsets={snapOffsets}
                    containerWidth={containerWidth}
                    onPress={handleArrowNavigationPress}
                    railId={railId || templateId}
                />
            )}

            <FlatList<RBParsedRail['sections'][number]>
                ref={flatlistRef}
                id={listDomId}
                horizontal
                onEndReached={handleOnEndReached}
                onEndReachedThreshold={0.2}
                nestedScrollEnabled
                keyExtractor={keyExtractor}
                style={[styles.container, { opacity: webRibbonHidden ? 0 : 1 }]}
                data={items}
                removeClippedSubviews={false}
                snapToAlignment="start"
                decelerationRate="fast"
                initialScrollIndex={isTabletOrWeb ? 0 : initialScrollIndex}
                snapToOffsets={snapOffsets}
                contentContainerStyle={containerStyles}
                renderItem={renderItem}
                showsHorizontalScrollIndicator={false}
                viewabilityConfig={viewabilityConfig}
                onViewableItemsChanged={onViewableItemsChanged}
                keyboardDismissMode="on-drag"
                getItemLayout={getItemLayout}
                onScroll={handleRibbonScroll}
                overScrollMode="never"
                bounces={false}
            />
        </View>
    );
}

export default Ribbon;

function keyExtractor(item: RBParsedRail['sections'][number], index: number) {
    const mixedItem = item as
        | MixedItem
        | RBProgram
        | CastCrewPerson
        | RecordingItem;
    const channelItem = item as RBChannel;
    const nowNextItem = item as NowNextData;

    if (mixedItem.id != null) {
        return mixedItem.id;
    }

    if (nowNextItem.channelId != null) {
        return nowNextItem.channelId;
    }

    if (channelItem.editorial && channelItem.editorial.id) {
        return channelItem.editorial.id;
    }

    return `no-id-fallback-${index}`;
}
